/*
 * PRD-037 — Sesion del borrador scoped al draft (app SEPARADA del partner).
 *
 * Abre una sesion de comercio sobre el draft (createDraftSession → JWT de comercio) y la coloca
 * en localStorage como TOKEN DE COMERCIO (`token` + `id_commerce`). El interceptor de axiosInstance
 * lo usa para autorizar las llamadas a los endpoints del panel (`/v1/comercio`, `/v1/categoria`, …)
 * sobre ese borrador. Al desmontar, restaura/limpia las llaves para NO contaminar el panel.
 *
 * Es el mecanismo que ya usaba PartnerDraftMenu para reusar el smart menu; se extrae aqui para
 * compartirlo con "Configurar comercio" (una sola fuente de verdad del patron de sesion-draft).
 */
import { useEffect, useState } from 'react';
import { createDraftSession } from '@services/partnerService';

const KEYS = ['token', 'id_commerce'] as const;

interface DraftSessionState {
  ready: boolean;
  error: string | null;
}

export const useDraftSession = (partnerToken: string | null, idComercio: string | undefined): DraftSessionState => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // `active` y `saved` son LOCALES a esta corrida del efecto (no refs compartidos). Al navegar
    // entre dos draft distintos en la MISMA ruta el componente NO se remonta: el efecto se re-dispara
    // por el cambio de `idComercio`, corriendo cleanup del draft anterior + este efecto para el nuevo.
    // Un ref compartido (`mounted.current = true`) seria re-armado por la corrida nueva, dejando pasar
    // el guard del async ya obsoleto → escribiria el token del draft VIEJO sobre el nuevo (stale write).
    // Con un booleano por-corrida, el cleanup del efecto viejo pone SU `active=false` y el async viejo
    // aborta aunque el efecto nuevo ya corra. Igual con `saved`: cada corrida captura sus propias
    // llaves previas y su cleanup las restaura, sin que la corrida nueva pise el snapshot de la vieja.
    let active = true;
    const saved: Record<string, string | null> = {};
    // Resetea `ready` al (re)correr el efecto. Sin esto, `ready` seguiria en `true` del draft anterior:
    // los hijos dispararian requests con el token-draft ya limpiado por el cleanup. Igual con `error`:
    // un fallo del draft anterior no debe pintar el estado de error del nuevo.
    setReady(false);
    setError(null);
    // Captura los valores previos SINCRONO, antes del await: si el componente se desmonta antes de
    // que createDraftSession resuelva, el cleanup debe restaurar el token/id_commerce reales (mismas
    // llaves compartidas) en vez de borrarlos por un snapshot vacio.
    KEYS.forEach((k) => {
      saved[k] = localStorage.getItem(k);
    });
    (async () => {
      if (!partnerToken || !idComercio) {
        if (active) setError('Sesion de partner no valida.');
        return;
      }
      try {
        const session = await createDraftSession(partnerToken, idComercio);
        // Si esta corrida se limpio durante el await (desmontaje o cambio de deps), el cleanup ya
        // restauro/limpio las llaves compartidas. Escribir aqui las re-contaminaria con un token
        // obsoleto DESPUES de navegar fuera (pisando la restauracion del cleanup). Aborta.
        if (!active) return;
        // Coloca el token de comercio del draft (los valores previos ya quedaron capturados).
        localStorage.setItem('token', session.jwt);
        localStorage.setItem('id_commerce', session.id_comercio);
        setReady(true);
      } catch {
        if (active) setError('No se pudo abrir la sesion del borrador.');
      }
    })();
    return () => {
      active = false;
      // Restaura/limpia para NO contaminar el panel con el token del draft.
      KEYS.forEach((k) => {
        const prev = saved[k];
        if (prev === null || prev === undefined) localStorage.removeItem(k);
        else localStorage.setItem(k, prev);
      });
    };
  }, [partnerToken, idComercio]);

  return { ready, error };
};
